import React, { useState, useCallback, useMemo } from 'react';
import { 
 Card, 
 Button, 
 Tag, 
 Space, 
 Typography, 
 Empty, 
 Skeleton, 
 Tooltip, 
 Badge,
 Dropdown,
 Modal,
 Progress,
 Divider,
 Row,
 Col,
 Alert,
 Statistic
} from 'antd';
import { 
 EditOutlined, 
 DeleteOutlined, 
 UserOutlined, 
 EyeOutlined,
 MoreOutlined,
 CalendarOutlined,
 InfoCircleOutlined,
 LockOutlined,
 UnlockOutlined,
 FileTextOutlined,
 ExclamationCircleOutlined,
 SafetyCertificateOutlined
} from '@ant-design/icons';
import { TitularDetalhado } from '../../../types/dashboard/mapeamentos.types';

const { Title, Text } = Typography;

interface TitularListProps {
 titulares: TitularDetalhado[];
 onEdit: (titular: TitularDetalhado) => void;
 onDelete: (id: string) => void;
 loading?: boolean;
 // Props para contexto de cliente
 clienteId?: string;
 clienteNome?: string;
}

const TitularList: React.FC<TitularListProps> = React.memo(({
 titulares,
 onEdit,
 onDelete,
 loading = false,
 clienteId,
 clienteNome
}) => {
 const [selectedTitular, setSelectedTitular] = useState<TitularDetalhado | null>(null);
 const [detailModalVisible, setDetailModalVisible] = useState(false);

 // Cálculos básicos de estatísticas
 const stats = useMemo(() => {
   if (!titulares.length) {
     return { total: 0, comDadosSensiveis: 0, mediaConformidade: 0 };
   }

   let comDadosSensiveis = 0;
   let somaConformidade = 0;

   titulares.forEach(titular => {
     // Verifica dados sensíveis
     const temSensiveis = titular.dados?.some(dado => 
       dado.dado_id && typeof dado.dado_id === 'object' && (dado.dado_id as any)?.tipo === 'sensivel'
     );
     if (temSensiveis) comDadosSensiveis++;

     // Calcula conformidade básica
     let progresso = 0;
     if (titular.categoria) progresso += 25;
     if (titular.dados && titular.dados.length > 0) progresso += 25;
     if (titular.finalidade_coleta) progresso += 25;
     if (titular.medidas_coleta || titular.medidas_processamento) progresso += 25;
     
     somaConformidade += progresso;
   });

   return {
     total: titulares.length,
     comDadosSensiveis,
     mediaConformidade: Math.round(somaConformidade / titulares.length)
   };
 }, [titulares]);

 const calcularProgressoMapeamento = useCallback((titular: TitularDetalhado) => {
   let progresso = 0;
   const totalCampos = 4;

   if (titular.categoria) progresso += 1;
   if (titular.dados && titular.dados.length > 0) progresso += 1;
   if (titular.finalidade_coleta) progresso += 1;
   if (titular.medidas_coleta || titular.medidas_processamento) progresso += 1;

   return Math.round((progresso / totalCampos) * 100);
 }, []);

 const temDadosSensiveis = useCallback((titular: TitularDetalhado) => {
   return titular.dados?.some(dado => 
     dado.dado_id && typeof dado.dado_id === 'object' && (dado.dado_id as any)?.tipo === 'sensivel'
   ) || false;
 }, []);

 const handleDelete = useCallback((titular: TitularDetalhado) => {
   Modal.confirm({
     title: 'Confirmar exclusão',
     content: `Tem certeza que deseja excluir o titular "${titular.categoria || 'Sem categoria'}"?`,
     okText: 'Sim, excluir',
     okType: 'danger',
     cancelText: 'Cancelar',
     onOk: () => onDelete(titular._id)
   });
 }, [onDelete]);

 const handleViewDetails = useCallback((titular: TitularDetalhado) => {
   setSelectedTitular(titular);
   setDetailModalVisible(true);
 }, []);

 const getMenuItems = useCallback((titular: TitularDetalhado) => [
   {
     key: 'view',
     icon: <EyeOutlined />,
     label: 'Visualizar Detalhes',
     onClick: () => handleViewDetails(titular)
   },
   {
     key: 'edit',
     icon: <EditOutlined />,
     label: 'Editar',
     onClick: () => onEdit(titular)
   },
   {
     type: 'divider' as const
   },
   {
     key: 'delete',
     icon: <DeleteOutlined />,
     label: 'Excluir',
     danger: true,
     onClick: () => handleDelete(titular)
   }
 ], [handleViewDetails, onEdit, handleDelete]);

 // Loading
 if (loading) {
   return (
     <div>
       {clienteId && (
         <Card size="small" style={{ marginBottom: 16 }}>
           <Skeleton active paragraph={{ rows: 1 }} />
         </Card>
       )}
       <div className="space-y-4">
         {[...Array(3)].map((_, i) => (
           <Card key={i}>
             <Skeleton active />
           </Card>
         ))}
       </div>
     </div>
   );
 }

 // Estado vazio
 if (titulares.length === 0) {
   return (
     <div>
       <Alert
         message={clienteId ? `Nenhum Titular Encontrado para ${clienteNome || 'Cliente'}` : "Nenhum Titular Cadastrado"}
         description={
           clienteId 
             ? `Não foram encontrados titulares de dados para ${clienteNome || 'este cliente'}.`
             : "Nenhum titular de dados foi encontrado. Comece cadastrando o primeiro titular."
         }
         type="warning"
         showIcon
         style={{ marginBottom: 16 }}
       />

       <Empty
         image={<UserOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />}
         description={
           <div>
             <Text strong>
               {clienteId ? `Nenhum titular encontrado` : 'Nenhum titular cadastrado'}
             </Text>
             <br />
             <Text type="secondary">
               {clienteId 
                 ? 'Cadastre titulares de dados para este cliente.'
                 : 'Adicione titulares de dados para começar o mapeamento LGPD.'
               }
             </Text>
           </div>
         }
       />
     </div>
   );
 }

 return (
   <>
     <div>
       {/* Estatísticas resumidas */}
       <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
         <Col xs={24} sm={8}>
           <Card hoverable>
             <Statistic
               title="Total de Titulares"
               value={stats.total}
               prefix={<UserOutlined style={{ color: '#1890ff' }} />}
               valueStyle={{ color: '#1890ff' }}
             />
           </Card>
         </Col>
         <Col xs={24} sm={8}>
           <Card hoverable>
             <Statistic
               title="Com Dados Sensíveis"
               value={stats.comDadosSensiveis}
               prefix={<LockOutlined style={{ color: '#f5222d' }} />}
               valueStyle={{ color: '#f5222d' }}
             />
           </Card>
         </Col>
         <Col xs={24} sm={8}>
           <Card hoverable>
             <Statistic
               title="Conformidade Média"
               value={stats.mediaConformidade}
               suffix="%"
               prefix={<SafetyCertificateOutlined style={{ 
                 color: stats.mediaConformidade >= 70 ? '#52c41a' : 
                        stats.mediaConformidade >= 50 ? '#faad14' : '#f5222d' 
               }} />}
               valueStyle={{ 
                 color: stats.mediaConformidade >= 70 ? '#52c41a' : 
                        stats.mediaConformidade >= 50 ? '#faad14' : '#f5222d' 
               }}
             />
           </Card>
         </Col>
       </Row>

       {/* Lista de titulares */}
       <div className="space-y-4">
         {titulares.map((titular) => {
           const progressoMapeamento = calcularProgressoMapeamento(titular);
           const temSensiveis = temDadosSensiveis(titular);
           
           return (
             <Card
               key={titular._id}
               hoverable
               style={{
                 border: temSensiveis ? '1px solid #ff7a45' : undefined
               }}
               actions={[
                 <Tooltip title="Visualizar" key="view">
                   <Button
                     type="text"
                     icon={<EyeOutlined />}
                     onClick={() => handleViewDetails(titular)}
                   />
                 </Tooltip>,
                 <Tooltip title="Editar" key="edit">
                   <Button
                     type="text"
                     icon={<EditOutlined />}
                     onClick={() => onEdit(titular)}
                   />
                 </Tooltip>,
                 <Dropdown
                   menu={{ items: getMenuItems(titular) }}
                   trigger={['click']}
                   key="more"
                 >
                   <Button
                     type="text"
                     icon={<MoreOutlined />}
                   />
                 </Dropdown>
               ]}
             >
               <div>
                 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                   <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                     <Title level={4} style={{ margin: 0 }}>
                       {titular.categoria || 'Categoria não informada'}
                     </Title>
                     {temSensiveis && (
                       <Badge
                         count={<LockOutlined style={{ color: '#faad14' }} />}
                         title="Contém dados sensíveis"
                       />
                     )}
                   </div>
                   <Tag color={progressoMapeamento >= 70 ? 'success' : progressoMapeamento >= 50 ? 'processing' : 'error'}>
                     {progressoMapeamento >= 70 ? 'Completo' : progressoMapeamento >= 50 ? 'Regular' : 'Incompleto'}
                   </Tag>
                 </div>
                 
                 <Space direction="vertical" size="small" style={{ width: '100%' }}>
                   <Space wrap>
                     {titular.quantidade && (
                       <Tag color="blue">
                         👥 {titular.quantidade}
                       </Tag>
                     )}

                     <Space size="small">
                       <CalendarOutlined />
                       <Text type="secondary">
                         {new Date(titular.created_at).toLocaleDateString('pt-BR')}
                       </Text>
                     </Space>
                   </Space>
                   
                   {titular.faixa_etaria && titular.faixa_etaria.length > 0 && (
                     <div>
                       <Text strong>👥 Faixa etária: </Text>
                       <Text>{titular.faixa_etaria.join(', ')}</Text>
                     </div>
                   )}
                   
                   {titular.dados && titular.dados.length > 0 && (
                     <Space>
                       <Text type="secondary">
                         📋 {titular.dados.length} tipo(s) de dados
                       </Text>
                       {temSensiveis && (
                         <Text type="warning">
                           🔒 Contém dados sensíveis
                         </Text>
                       )}
                     </Space>
                   )}

                   {/* Progress de conformidade */}
                   <div style={{ marginTop: 8 }}>
                     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                       <Text type="secondary" style={{ fontSize: '12px' }}>
                         Conformidade LGPD
                       </Text>
                       <Text type="secondary" style={{ fontSize: '12px' }}>
                         {progressoMapeamento}%
                       </Text>
                     </div>
                     <Progress
                       percent={progressoMapeamento}
                       size="small"
                       strokeColor={
                         progressoMapeamento >= 70 ? '#52c41a' :
                         progressoMapeamento >= 50 ? '#faad14' : '#ff4d4f'
                       }
                     />
                   </div>
                 </Space>
               </div>
             </Card>
           );
         })}
       </div>
     </div>

     {/* Modal de Detalhes */}
     <Modal
       title={
         <Space>
           <UserOutlined />
           Detalhes do Titular: {selectedTitular?.categoria || 'Sem categoria'}
           {clienteId && (
             <Tag color="blue" style={{ marginLeft: 8 }}>
               {clienteNome || 'Cliente'}
             </Tag>
           )}
         </Space>
       }
       open={detailModalVisible}
       onCancel={() => {
         setDetailModalVisible(false);
         setSelectedTitular(null);
       }}
       footer={[
         <Button key="close" onClick={() => setDetailModalVisible(false)}>
           Fechar
         </Button>,
         <Button 
           key="edit" 
           type="primary" 
           icon={<EditOutlined />}
           onClick={() => {
             if (selectedTitular) {
               onEdit(selectedTitular);
               setDetailModalVisible(false);
             }
           }}
         >
           Editar
         </Button>
       ]}
       width={800}
     >
       {selectedTitular && (
         <Space direction="vertical" size="middle" style={{ width: '100%' }}>
           {/* Status de conformidade */}
           <Card size="small" style={{ backgroundColor: '#f6ffed' }}>
             <Row align="middle">
               <Col flex="auto">
                 <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                   <SafetyCertificateOutlined style={{ color: '#52c41a' }} />
                   <span>
                     <strong>Conformidade:</strong> {calcularProgressoMapeamento(selectedTitular)}%
                   </span>
                 </div>
               </Col>
               <Col>
                 <Progress 
                   type="circle" 
                   percent={calcularProgressoMapeamento(selectedTitular)} 
                   width={40}
                   strokeColor={
                     calcularProgressoMapeamento(selectedTitular) >= 70 ? '#52c41a' :
                     calcularProgressoMapeamento(selectedTitular) >= 50 ? '#faad14' : '#ff4d4f'
                   }
                 />
               </Col>
             </Row>
           </Card>

           <div>
             <Title level={5}>📊 Informações Básicas</Title>
             <Space direction="vertical" size="small">
               <Text><strong>Categoria:</strong> {selectedTitular.categoria || 'Não informado'}</Text>
               <Text><strong>Quantidade:</strong> {selectedTitular.quantidade || 'Não informado'}</Text>
               {selectedTitular.faixa_etaria && selectedTitular.faixa_etaria.length > 0 && (
                 <Text><strong>Faixa Etária:</strong> {selectedTitular.faixa_etaria.join(', ')}</Text>
               )}
               <Text><strong>Data de Cadastro:</strong> {new Date(selectedTitular.created_at).toLocaleDateString('pt-BR')}</Text>
             </Space>
           </div>

           <Divider />

           <div>
             <Title level={5}>📋 Dados Pessoais ({selectedTitular.dados?.length || 0})</Title>
             {selectedTitular.dados && selectedTitular.dados.length > 0 ? (
               <Space direction="vertical" size="small">
                 {selectedTitular.dados.map((dado, index) => {
                   const dadoObj = dado.dado_id as any;
                   const isSensivel = dadoObj?.tipo === 'sensivel';
                   
                   return (
                     <div key={index} style={{ 
                       padding: 12, 
                       background: isSensivel ? '#fff2e8' : '#f5f5f5', 
                       borderRadius: 6,
                       border: isSensivel ? '1px solid #ffb366' : '1px solid #d9d9d9'
                     }}>
                       <Space>
                         {isSensivel ? (
                           <LockOutlined style={{ color: '#faad14' }} />
                         ) : (
                           <UnlockOutlined style={{ color: '#52c41a' }} />
                         )}
                         <Text strong>
                           {dadoObj?.nome || 'Dado não especificado'}
                         </Text>
                         <Tag color={isSensivel ? 'orange' : 'blue'}>
                           {dadoObj?.tipo || 'comum'}
                         </Tag>
                       </Space>
                       {dado.motivo && (
                         <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                           <strong>Motivo:</strong> {dado.motivo}
                         </Text>
                       )}
                     </div>
                   );
                 })}
               </Space>
             ) : (
               <Alert
                 message="Nenhum dado pessoal cadastrado"
                 description="Este titular não possui dados pessoais mapeados."
                 type="warning"
                 showIcon
               />
             )}
           </div>

           <Divider />

           <div>
             <Title level={5}>🎯 Finalidades</Title>
             <Space direction="vertical" size="small">
               {selectedTitular.finalidade_coleta && (
                 <div>
                   <Text strong>Finalidade da Coleta:</Text>
                   <br />
                   <Text style={{ whiteSpace: 'pre-wrap' }}>{selectedTitular.finalidade_coleta}</Text>
                 </div>
               )}
               {selectedTitular.finalidade_processamento && (
                 <div>
                   <Text strong>Finalidade do Processamento:</Text>
                   <br />
                   <Text style={{ whiteSpace: 'pre-wrap' }}>{selectedTitular.finalidade_processamento}</Text>
                 </div>
               )}
               {!selectedTitular.finalidade_coleta && !selectedTitular.finalidade_processamento && (
                 <Alert
                   message="Nenhuma finalidade cadastrada"
                   description="Adicione finalidades para melhorar a conformidade LGPD."
                   type="info"
                   showIcon
                 />
               )}
             </Space>
           </div>
         </Space>
       )}
     </Modal>
   </>
 );
});

TitularList.displayName = 'TitularList';

export default TitularList;